import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { useThree } from '@react-three/fiber'
import * as THREE from 'three'

function projectToTerrain(points, terrainMeshes, clearance) {
  terrainMeshes.forEach(mesh => mesh.updateWorldMatrix(true, false))
  const raycaster = new THREE.Raycaster()
  raycaster.ray.direction.set(0, -1, 0)
  return points.map(source => {
    raycaster.ray.origin.set(source.x, 80, source.z)
    const hit = raycaster.intersectObjects(terrainMeshes, false)[0]
    if (!hit) return source.clone()
    // Normal offset is safer than Y-only offset on steep Flatiron faces.
    const normal = hit.face?.normal?.clone() ?? new THREE.Vector3(0, 1, 0)
    normal.transformDirection(hit.object.matrixWorld)
    return hit.point.clone().addScaledVector(normal, clearance)
  })
}

/**
 * Physical rope for a world-space route. routePoints should contain authored X/Z;
 * Y is ignored and sampled from the actual local terrain.
 */
export function TerrainRope({
  routePoints,
  color = '#f4c735',
  clearance = 0.12,
  radius = 0.055,
  radialSegments = 6,
  samples = 320,
  opacity = 1,
  onProjected,
}) {
  const scene = useThree(state => state.scene)
  const [projected, setProjected] = useState(null)

  const sourcePoints = useMemo(() => {
    if (routePoints.length < 2) return []
    const controls = routePoints.map(point => point.isVector3
      ? point.clone()
      : new THREE.Vector3(point[0], point[1] ?? 0, point[2]))
    return new THREE.CatmullRomCurve3(controls, false, 'centripetal', 0.35)
      .getSpacedPoints(samples)
  }, [routePoints, samples])

  useLayoutEffect(() => {
    const terrainMeshes = []
    scene.traverse(object => {
      if (object.isMesh && object.userData.terrainSurface) terrainMeshes.push(object)
    })
    if (!terrainMeshes.length || sourcePoints.length < 2) return
    scene.updateMatrixWorld(true)
    const next = projectToTerrain(sourcePoints, terrainMeshes, clearance)
    setProjected(next)
    onProjected?.(next)
  }, [clearance, onProjected, scene, sourcePoints])

  const geometry = useMemo(() => {
    if (!projected || projected.length < 2) return null
    const curve = new THREE.CatmullRomCurve3(projected, false, 'centripetal', 0.18)
    return new THREE.TubeGeometry(curve, projected.length - 1, radius, radialSegments, false)
  }, [projected, radius, radialSegments])

  useEffect(() => () => geometry?.dispose(), [geometry])

  if (!geometry) return null
  return <mesh geometry={geometry} castShadow receiveShadow>
    <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.38} roughness={0.68} metalness={0} transparent={opacity < 1} opacity={opacity} depthWrite={opacity > .5} />
  </mesh>
}
